// Uang Muka Vendor — daftar + pengajuan (#4b-3c, layar 1)
//
// Uang muka adalah satu-satunya jalur yang mengeluarkan uang SEBELUM barang
// datang. Karena itu layar ini tidak sekadar mendaftar: ia menampilkan dua
// hal yang menentukan keputusan SEBELUM tombol ditekan — sisa plafon PO, dan
// mana uang muka yang sudah terlalu lama menggantung.
//
// NOL KONSTANTA BISNIS. Daftar status, status mana yang menahan plafon,
// rasio plafon, ambang umur, ambang panjang alasan, PO yang layak diajukan,
// dan wewenang pengguna ini SEMUANYA dari GET /purchasing/meta/dp.
//
// TIGA HAL DI META YANG MUSTAHIL DITEBAK UI — jangan pernah disalin ke sini:
//   1. `active_statuses` memuat WRITTEN_OFF (Q58 — write-off mengesahkan
//      kerugiannya, bukan menghapus uangnya) tapi TIDAK memuat
//      PENDING_APPROVAL. Asimetri itu keputusan sadar Owner. UI yang menebak
//      "yang belum ditolak berarti menahan plafon" akan salah di kedua ujung.
//   2. Ambang panjang alasan BERBEDA per aksi: 10 karakter untuk pengajuan
//      dan write-off, 3 untuk sisanya.
//   3. `eligibility.po_statuses_for_new_dp` — PO status apa yang layak
//      diajukan uang muka; aturannya hidup di service.
//
// Q81 (9 Agustus 2026) MENCABUT syarat "vendor pernah punya PO
// FULLY_RECEIVED" (Q16/Q56). Vendor cukup aktif. Kalau menemukan sisa aturan
// itu di kode atau teks mana pun, itu jejak yang tertinggal — bukan aturan
// yang hidup.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ambilSemua } from "../../lib/ambilSemua";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge, MetaStatusBadge, toneFromMeta } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  HandCoins, RefreshCw, Plus, AlertCircle, ArrowRight, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, todayISO } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  listDPs, createDP, getDPMeta, getPO, apiErrorMessage,
} from "../../lib/purchasingApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import {
  cariVendor, ambilVendor, labelVendor, cariPo, labelPo,
} from "../../lib/sumberPilihan";
import { BELUM_SELESAI, paramStatus, saringanStatus } from "../../lib/saringanPurchasing";
import SisaUangKembaliPanel from "./SisaUangKembaliPanel";
import UangLebihPemasokPanel from "./UangLebihPemasokPanel";
import { useAuth } from "../../contexts/AuthContext";
import { SISA_UANG_KEMBALI_ROLES, UANG_LEBIH_ROLES } from "../../lib/roles";

//: Cermin `URUT_SAH_DP` di backend/services/vendor_dp_service.py.
const URUT_SAH = ["dp_date", "dp_number", "amount", "status", "created_at"];

//: Lencana status DP. Tone dikirim backend per status; terjemahan tone →
//: warna hidup di satu tempat (StatusBadge, Q68).
export function dpStatusBadge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

/** Umur uang muka. `age_days` + `is_aging` DIHITUNG BACKEND dari `paid_at`
 *  (umur sebuah aset dimulai saat ia jadi aset) — jangan dihitung ulang di
 *  sini dari tanggal pengajuan, angkanya akan berbeda dan yang salah adalah
 *  yang di layar. */
export function badgeUmur(dp) {
  if (dp?.age_days === null || dp?.age_days === undefined) return null;
  return dp.is_aging
    ? { tone: "danger", label: `Menggantung ${dp.age_days} hari` }
    : { tone: "neutral", label: `${dp.age_days} hari` };
}

/** Jumlah uang muka yang MENAHAN plafon PO. Daftar statusnya dari
 *  meta.active_statuses — lihat catatan 1 di kepala berkas. */
export function totalMenahanPlafon(meta, rows) {
  const aktif = meta?.active_statuses || null;
  if (!aktif) return null;                 // meta belum termuat: jangan menebak
  return (rows || []).reduce(
    (s, d) => s + (aktif.includes(d.status) ? Number(d.amount) || 0 : 0), 0);
}

/** Sisa plafon sebuah PO: rasio × nilai PO − yang sudah menahan.
 *  Nilai PO NULL = plafon tidak bisa dihitung sama sekali (backend menolak
 *  dengan DP_PO_TOTAL_UNKNOWN); dibedakan dari sisa nol. */
export function hitungPlafon(meta, po, dpRows) {
  const rasio = meta?.limits?.cap_ratio;
  const total = po?.total_amount;
  if (rasio === undefined || rasio === null) return null;
  if (total === null || total === undefined) {
    return { tidakDiketahui: true, plafon: null, terpakai: null, sisa: null };
  }
  const terpakai = totalMenahanPlafon(meta, dpRows);
  if (terpakai === null) return null;
  const plafon = Math.round(Number(total) * rasio * 100) / 100;
  return {
    tidakDiketahui: false,
    plafon,
    terpakai,
    sisa: Math.round((plafon - terpakai) * 100) / 100,
  };
}

export default function VendorDPs() {
  const { roleCode } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  //: Vendor saringan terpilih (objek) — untuk nama di chip.
  const [vendorSaring, setVendorSaring] = useState(null);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md). Saringan bawaan
  // "Belum selesai" (tanpa Terpotong/Dikreditkan/Batal) — terlihat sebagai chip,
  // bisa dihapus. "Menggantung" mengunci status Dibayar di backend: ia dan status
  // tertentu ditulis BERSAMAAN (setBeberapaSaringan), yang terakhir dipilih menang.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "dp_date", arah: "desc" },
    saringan: {
      status: saringanStatus(meta),
      vendor: { label: "Vendor", param: "vendor_id",
                tampil: (v) => (vendorSaring?.id === v ? vendorSaring.name : "vendor terpilih") },
      umur: { label: "Umur", param: "aging_only",
              tampil: () => `Menggantung lebih dari ${meta?.limits?.aging_days ?? 30} hari` },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan, nilaiSaringan } = daftar;
  const statusFilter = nilaiSaringan.status;
  const vendorFilter = nilaiSaringan.vendor;
  const agingOnly = nilaiSaringan.umur === "true";
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramStatus(paramSaringan), ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listDPs(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);
  //: status tertentu (bukan "Belum selesai") bertabrakan dengan "menggantung".
  const statusTertentu = (v) => !!v && v !== BELUM_SELESAI;

  useEffect(() => {
    if (!vendorFilter) { setVendorSaring(null); return; }
    if (vendorSaring?.id === vendorFilter) return;
    ambilVendor(vendorFilter).then(setVendorSaring).catch((e) => {
      setVendorSaring(null); toast.error(apiErrorMessage(e, "Nama vendor saringan gagal dibaca."));
    });
  }, [vendorFilter, vendorSaring?.id]);

  // Antrean kerja — masing-masing hanya untuk yang berwenang menanganinya.
  const [antreanSetuju, setAntreanSetuju] = useState(null);
  const [antreanBayar, setAntreanBayar] = useState(null);
  const [antreanTua, setAntreanTua] = useState(null);

  // ---- Dialog pengajuan ------------------------------------------------
  const [ajukanOpen, setAjukanOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  //: PO terpilih dari PemilihCari (objek) — dicari di server per vendor + status layak.
  const [poPilih, setPoPilih] = useState(null);
  //: Kegagalan memuat PO harus KELIHATAN. Sebelumnya ditelan `.catch(() => [])`
  //: dan layarnya jadi tidak terbedakan dari "vendor ini memang tidak punya PO
  //: yang layak" — orang lalu menyimpulkan syaratnya yang salah, padahal
  //: permintaannya yang gagal.
  const [poError, setPoError] = useState(null);
  //: Jalan masuk dari layar detail PO: barisnya diambil langsung lewat getPO,
  //: BUKAN dicari di daftar dropdown. `poList` hanya memuat 200 baris pertama
  //: per status yang layak, jadi `find()` bisa meleset diam-diam pada PO yang
  //: berada di luar potongan itu — dan melesetnya terlihat seperti "plafon
  //: belum termuat", bukan seperti kesalahan.
  const [poTerkunci, setPoTerkunci] = useState(null);
  const [poTerkunciLoading, setPoTerkunciLoading] = useState(false);
  //: Pilihan bertingkat di layar daftar: vendor dulu, baru PO miliknya.
  const [vendorPilihObj, setVendorPilihObj] = useState(null);
  const vendorPilih = vendorPilihObj?.id || "";
  const [form, setForm] = useState({});
  //: DP yang sudah ada pada PO terpilih — dasar hitungan sisa plafon.
  const [dpPo, setDpPo] = useState(null);
  const [dpPoLoading, setDpPoLoading] = useState(false);

  const perm = meta?.permissions || {};

  useEffect(() => {
    getDPMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info uang muka.")));
    // Vendor TIDAK lagi dimuat seluruhnya — saringan & formulir mencari di server.
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDPs(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar uang muka."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  // Angka antrean dari `total` hasil filter BACKEND, bukan dihitung dari
  // halaman yang sedang tampil — yang kedua hanya benar di halaman 1 lalu
  // diam-diam salah (pola PurchaseOrders / VendorInvoices).
  const refreshAntrean = useCallback(() => {
    if (perm.approve) {
      listDPs({ status: "PENDING_APPROVAL", page: 1, page_size: 1 })
        .then(r => setAntreanSetuju(r.data.total || 0))
        .catch((e) => { setAntreanSetuju(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    }
    if (perm.pay) {
      listDPs({ status: "APPROVED", page: 1, page_size: 1 })
        .then(r => setAntreanBayar(r.data.total || 0))
        .catch((e) => { setAntreanBayar(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    }
    // Umur menggantung relevan untuk SEMUA pembaca, bukan satu role: uang
    // muka yang menua adalah aset yang belum kembali, dan yang menagihnya
    // bisa siapa saja yang memegang hubungan vendor.
    listDPs({ aging_only: true, page: 1, page_size: 1 })
      .then(r => setAntreanTua(r.data.total || 0))
      .catch((e) => { setAntreanTua(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
  }, [perm.approve, perm.pay]);
  useEffect(() => { refreshAntrean(); }, [refreshAntrean, items]);

  const adaFilter = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "dp_number", judul: "Nomor" },
    { kunci: "dp_date", judul: "Tanggal", ambil: (d) => formatDate(d.dp_date) },
    { kunci: "vendor", judul: "Vendor", ambil: (d) => d.vendor?.name || "" },
    { kunci: "po", judul: "PO", ambil: (d) => d.po?.po_number || "" },
    { kunci: "amount", judul: "Nominal" },
    { kunci: "age_days", judul: "Umur (hari)" },
    { kunci: "status", judul: "Status", ambil: (d) => dpStatusBadge(meta, d.status).label },
  ];

  // ---- Pengajuan -------------------------------------------------------

  //: PO milik SATU vendor yang layak menerima uang muka — DICARI di server
  //: (PemilihCari, 28 Sep 2026): vendor + `status_in` = meta.eligibility.
  //: po_statuses_for_new_dp dalam SATU panggilan. Dulu: seluruh PO vendor per
  //: status ditarik ke browser (ambilSemua), dan daftarnya menua tiap tahun.
  //: Status dari meta — status baru di backend ikut sendirinya.
  const cariPoLayak = useCallback(async (kata) => {
    const layak = meta?.eligibility?.po_statuses_for_new_dp || [];
    if (!vendorPilih || layak.length === 0) return [];
    const hasil = await cariPo(kata, { vendor_id: vendorPilih, status_in: layak.join(",") });
    // PO tanpa vendor ditolak backend (PO_VENDOR_NOT_ASSIGNED) — tidak ditawarkan.
    return hasil.filter(p => p.vendor_id);
  }, [meta, vendorPilih]);

  const bukaAjukan = useCallback((poId = "") => {
    setForm({
      po_id: poId,
      amount: "",
      dp_date: todayISO(),
      request_reason: "",
      notes: "",
    });
    setDpPo(null);
    setPoPilih(null);
    setPoError(null);
    setVendorPilihObj(null);
    setPoTerkunci(null);
    setAjukanOpen(true);

    if (!poId) return;
    // Jalur detail PO: PO-nya dikunci dan barisnya diambil langsung.
    setPoTerkunciLoading(true);
    getPO(poId)
      .then(r => setPoTerkunci(r.data))
      .catch(e => {
        setPoTerkunci(null);
        setPoError(apiErrorMessage(e, "Gagal memuat PO yang dipilih."));
      })
      .finally(() => setPoTerkunciLoading(false));
  }, []);

  //: Vendor berganti → daftar PO ikut, dan PO yang terlanjur terpilih dibuang
  //: supaya tidak tertinggal milik vendor sebelumnya.
  useEffect(() => {
    if (!ajukanOpen || poTerkunci || poTerkunciLoading) return;
    setForm(f => (f.po_id ? { ...f, po_id: "" } : f));
    setPoPilih(null);
  }, [ajukanOpen, vendorPilih, poTerkunci, poTerkunciLoading]);

  //: Jalan masuk dari layar PO (?po_id=<id>) — supaya "Ajukan Uang Muka" di
  //: panel PO mendarat pada formulir yang sudah terisi PO-nya.
  const poParam = sp.get("po_id") || "";
  useEffect(() => {
    if (poParam && meta && perm.create) {
      bukaAjukan(poParam);
      const next = new URLSearchParams(sp);
      next.delete("po_id");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poParam, meta, perm.create]);

  //: Begitu PO dipilih, uang muka yang sudah ada di PO itu diambil — dari
  //: situlah sisa plafon dihitung DAN DITAMPILKAN sebelum tombol ditekan.
  //: Backend tetap penjaganya (DP_EXCEEDS_PO_CAP); yang ditambahkan di sini
  //: cuma supaya orang tidak mengetik angka yang pasti ditolak.
  useEffect(() => {
    if (!ajukanOpen || !form.po_id) { setDpPo(null); return; }
    let batal = false;
    setDpPoLoading(true);
    ambilSemua(listDPs, { po_id: form.po_id })
      .then(items => { if (!batal) setDpPo(items); })
      .catch(() => { if (!batal) setDpPo([]); })
      .finally(() => { if (!batal) setDpPoLoading(false); });
    return () => { batal = true; };
  }, [ajukanOpen, form.po_id]);

  //: PO terkunci menang atas hasil pencarian di daftar — di jalur detail PO,
  //: barisnya memang tidak pernah dimuat ke `poList`.
  const poTerpilih = useMemo(
    () => (poTerkunci && poTerkunci.id === form.po_id
      ? poTerkunci
      : (poPilih && poPilih.id === form.po_id ? poPilih : null)),
    [poTerkunci, poPilih, form.po_id]);
  const plafon = useMemo(
    () => (poTerpilih && dpPo ? hitungPlafon(meta, poTerpilih, dpPo) : null),
    [meta, poTerpilih, dpPo]);

  const minAlasan = (meta?.transitions || [])
    .find(t => t.action === "create")?.min_reason_length || 10;
  const nominal = Number(form.amount);
  const nominalSah = Number.isFinite(nominal) && nominal > 0;
  const alasanKurang = (form.request_reason || "").trim().length < minAlasan;
  const lewatPlafon = !!(plafon && !plafon.tidakDiketahui && nominalSah
    && nominal > plafon.sisa);
  //: `plafon` WAJIB ada, bukan sekadar "tidak berstatus tidakDiketahui".
  //: `null` berarti plafonnya belum bisa dihitung sama sekali — meta belum
  //: termuat, baris PO tidak ketemu, atau daftar DP-nya gagal diambil. Dulu
  //: keadaan itu lolos dan tombolnya hidup tanpa satu angka pun di layar,
  //: sehingga penolakan baru datang dari backend setelah formulir diisi penuh.
  const bisaKirim = !!form.po_id && nominalSah && !alasanKurang
    && !!form.dp_date && !lewatPlafon
    && !!plafon && !plafon.tidakDiketahui;

  const kirimAjukan = async () => {
    setBusy(true);
    try {
      const r = await createDP({
        po_id: form.po_id,
        amount: nominal,
        dp_date: form.dp_date,
        request_reason: form.request_reason.trim(),
        notes: form.notes?.trim() || null,
      });
      toast.success(
        `Uang muka ${r.data.dp_number} diajukan — menunggu persetujuan `
        + `Kepala Cabang/Owner.`);
      setAjukanOpen(false);
      load();
      refreshAntrean();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengajukan uang muka."));
    } finally {
      setBusy(false);
    }
  };

  const bukaAntrean = (st) => daftar.setBeberapaSaringan({ status: st, umur: "" });

  return (
    <div className="space-y-6" data-testid="dp-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <HandCoins className="h-6 w-6" /> Uang Muka Vendor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pembayaran di muka ke vendor, maksimal{" "}
            {meta?.limits?.cap_ratio
              ? `${Math.round(meta.limits.cap_ratio * 100)}%`
              : "sebagian"}{" "}
            dari nilai PO. Wajib disetujui Kepala Cabang/Owner sebelum Finance
            mengeluarkan uang.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="dp-refresh"
                  onClick={() => { load(); refreshAntrean(); }}>
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {perm.create && (
            <Button size="sm" data-testid="dp-create-btn"
                    onClick={() => bukaAjukan()}>
              <Plus className="h-4 w-4 mr-1" /> Ajukan Uang Muka
            </Button>
          )}
        </div>
      </div>

      {/* K214 / BUSINESS-87: sisa uang kembali uang muka yang ditagih balik — ditagih Admin Purchasing. */}
      {SISA_UANG_KEMBALI_ROLES.includes(roleCode) && (
        <SisaUangKembaliPanel sumber="UANG_MUKA_VENDOR" testId="dp-sisa-uang-kembali" />
      )}
      {/* K215 / BUSINESS-90: uang lebih dari pemasok — dipilih Admin Purchasing, disetujui KC, dikembalikan Finance. */}
      {UANG_LEBIH_ROLES.includes(roleCode) && (
        <UangLebihPemasokPanel sumber="UANG_MUKA_VENDOR" testId="dp-uang-lebih" />
      )}

      {/* Antrean persetujuan */}
      {perm.approve && antreanSetuju > 0 && statusFilter !== "PENDING_APPROVAL" && (
        <button type="button" onClick={() => bukaAntrean("PENDING_APPROVAL")}
                data-testid="dp-approval-banner"
                className="w-full text-left bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3
                           hover:bg-warning-soft/70 transition flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>{antreanSetuju} uang muka menunggu persetujuan Anda.</strong>{" "}
            Finance belum bisa mengeluarkan uang sebelum disetujui.
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      {/* Antrean Finance */}
      {perm.pay && antreanBayar > 0 && statusFilter !== "APPROVED" && (
        <button type="button" onClick={() => bukaAntrean("APPROVED")}
                data-testid="dp-payment-banner"
                className="w-full text-left bg-info-soft text-info-soft-foreground border border-info/40 rounded p-3
                           hover:bg-info-soft/70 transition flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>{antreanBayar} uang muka sudah disetujui dan menunggu dibayar.</strong>
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      {/* Umur menggantung — merah karena A2: uang muka lewat ambang = butuh
          tindakan, bukan sekadar informasi. */}
      {antreanTua > 0 && !agingOnly && (
        <button type="button"
                onClick={() => daftar.setBeberapaSaringan({ status: "", umur: "true" })}
                data-testid="dp-aging-banner"
                className="w-full text-left bg-danger-soft text-danger-soft-foreground border border-danger/40 rounded p-3
                           hover:bg-danger-soft/70 transition flex items-center gap-3">
          <Clock className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>
              {antreanTua} uang muka sudah dibayar tapi menggantung lebih dari{" "}
              {meta?.limits?.aging_days ?? 30} hari.
            </strong>{" "}
            Uang muka adalah titipan yang belum kembali — potongkan ke tagihan,
            tagih balik, atau selesaikan.
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor uang muka, vendor, atau nomor PO…" testId="dp-palang"
                    ekspor={{ nama: "uang_muka_vendor", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="dp-status-filter" aria-label="Saring status"
                onChange={(e) => {
                  // Penyaring umur mengunci status PAID di backend. Status TERTENTU
                  // + umur = dua .eq("status") yang bertabrakan → KOSONG tanpa
                  // penjelasan. Yang terakhir dipilih menang (satu tulisan alamat).
                  const v = e.target.value;
                  if (statusTertentu(v)) daftar.setBeberapaSaringan({ status: v, umur: "" });
                  else daftar.setSaringan("status", v);
                }}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          <option value={BELUM_SELESAI}>Belum selesai</option>
          {(meta?.statuses || []).map(s =>
            <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <div className="w-56">
          <PemilihCari value={vendorSaring} bolehKosong
                       onChange={(v) => { setVendorSaring(v); daftar.setSaringan("vendor", v?.id || ""); }}
                       cari={cariVendor} label={labelVendor}
                       placeholder="Semua vendor" placeholderCari="Ketik nama atau kode vendor…"
                       testId="dp-vendor-filter" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={agingOnly} data-testid="dp-aging-filter"
                 onChange={(e) => daftar.setBeberapaSaringan(e.target.checked
                   // aging_only mengunci status PAID — status TERTENTU dilepas.
                   ? { umur: "true", ...(statusTertentu(statusFilter) ? { status: "" } : {}) }
                   : { umur: "" })}
                 className="h-4 w-4 rounded border" />
          Menggantung lebih dari {meta?.limits?.aging_days ?? 30} hari
        </label>
      </PalangDaftar>
      {(statusTertentu(statusFilter) || agingOnly) && (
      <div className="-mt-4">
        {statusTertentu(statusFilter) && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="dp-status-help">
            {dpStatusBadge(meta, statusFilter).description}
          </p>
        )}
        {agingOnly && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="dp-aging-help">
            Umur dihitung sejak uang benar-benar keluar, bukan sejak diajukan — dan hanya
            berlaku untuk yang berstatus{" "}
            {dpStatusBadge(meta, meta?.limits?.aging_applies_to_status || "PAID").label}.
          </p>
        )}
      </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="dp-loading">
            {[...Array(5)].map((_, i) =>
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-2"
               data-testid="dp-empty">
            {adaFilter ? (
              <p>
                Tidak ada uang muka yang cocok dengan pencarian atau saringan ini.
                Coba hapus saringannya.
              </p>
            ) : (
              <>
                <p>
                  Belum ada uang muka vendor. Uang muka diajukan dari sebuah PO
                  yang sudah terbit dan vendornya masih aktif.
                </p>
                {perm.create ? (
                  <Button variant="outline" size="sm" data-testid="dp-empty-create"
                          onClick={() => bukaAjukan()}>
                    Ajukan Uang Muka
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" data-testid="dp-empty-goto-po"
                          onClick={() => navigate("/purchasing/po")}>
                    Lihat Purchase Order
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftar} kunci="dp_number" className="px-4 py-3 font-medium">Nomor</ThUrut>
                  <ThUrut daftar={daftar} kunci="dp_date" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">PO</th>
                  <ThUrut daftar={daftar} kunci="amount" align="right" className="px-4 py-3 font-medium">Nominal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Umur</th>
                  <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                </tr>
              </thead>
              <tbody className="divide-y">
                {daftar.tampil.map(d => {
                  const tz = tzForRegion(d.region_id || d.region?.id);
                  const umur = badgeUmur(d);
                  return (
                    <tr key={d.id}
                        className={`hover:bg-muted/50 cursor-pointer ${d.is_aging ? "bg-danger-soft/40" : ""}`}
                        onClick={() => navigate(`/purchasing/dp/${d.id}`)}
                        data-testid={`dp-row-${d.dp_number}`}>
                      <td className="px-4 py-3 font-medium">{d.dp_number}</td>
                      <td className="px-4 py-3">{formatDate(d.dp_date, tz)}</td>
                      <td className="px-4 py-3">{d.vendor?.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {d.po?.po_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">{formatIDR(d.amount)}</td>
                      <td className="px-4 py-3">
                        {umur ? (
                          <StatusBadge tone={umur.tone}
                                       testId={`dp-umur-${d.dp_number}`}>
                            {umur.label}
                          </StatusBadge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <MetaStatusBadge meta={meta} code={d.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="dp-paginasi" />
      </Card>

      {/* ------------------------- Dialog pengajuan ------------------------ */}
      <Dialog open={ajukanOpen} onOpenChange={(o) => !busy && setAjukanOpen(o)}>
        <DialogContent className="max-w-xl" data-testid="dp-create-dialog">
          <DialogHeader>
            <DialogTitle>Ajukan uang muka vendor</DialogTitle>
            <DialogDescription>
              Pengajuan ini belum mengeluarkan uang. Setelah disetujui Kepala
              Cabang/Owner, Finance yang mengeksekusi pembayarannya.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            {/* Jalur detail PO: PO dikunci, tidak ada yang perlu dicari. */}
            {(poTerkunci || poTerkunciLoading) ? (
              <div>
                <label className="text-sm font-medium">Purchase Order</label>
                <div className="mt-1 rounded border bg-muted/40 px-3 py-2 text-sm"
                     data-testid="dp-form-po-terkunci">
                  {poTerkunciLoading ? (
                    <span className="text-muted-foreground">Memuat PO...</span>
                  ) : (
                    <>
                      <span className="font-medium">{poTerkunci.po_number}</span>
                      {" · "}{poTerkunci.vendor?.name || "vendor?"}
                      {" · "}{formatIDR(poTerkunci.total_amount)}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div>
                  <span className="text-sm font-medium">Vendor</span>
                  <div className="mt-1">
                    <PemilihCari value={vendorPilihObj} onChange={(v) => setVendorPilihObj(v || null)}
                                 cari={(kata) => cariVendor(kata, { is_active: true })} label={labelVendor}
                                 tampilAwal placeholder="— pilih vendor —"
                                 placeholderCari="Ketik nama atau kode vendor…"
                                 testId="dp-form-vendor" />
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium">Purchase Order</span>
                  <div className="mt-1">
                    {/* `key` = vendor: ganti vendor → pemilih PO mulai bersih. */}
                    <PemilihCari key={vendorPilih || "-"} value={poPilih}
                                 disabled={!vendorPilih}
                                 onChange={(po) => { setPoPilih(po || null); setForm(f => ({ ...f, po_id: po?.id || "" })); }}
                                 cari={cariPoLayak} label={labelPo}
                                 keterangan={(po) => formatIDR(po?.total_amount)}
                                 tampilAwal
                                 placeholder={vendorPilih ? "— pilih PO —" : "— pilih vendor dulu —"}
                                 placeholderCari="Ketik nomor PO…"
                                 testId="dp-form-po" />
                  </div>
                  {poError ? (
                    <p className="text-xs text-danger-soft-foreground mt-1"
                       data-testid="dp-form-po-error">{poError}</p>
                  ) : (vendorPilih && (
                    <p className="text-xs text-muted-foreground mt-1"
                       data-testid="dp-form-po-empty">
                      Hanya PO vendor ini yang bisa menerima uang muka: PO masih
                      berjalan{meta?.eligibility?.po_statuses_for_new_dp
                        ? ` (${meta.eligibility.po_statuses_for_new_dp.join(" / ")})`
                        : ""}{" "}
                      dan vendornya sudah ditetapkan.
                    </p>
                  ))}
                </div>
              </>
            )}
            {poTerkunci && poError && (
              <p className="text-xs text-danger-soft-foreground" data-testid="dp-form-po-error">
                {poError}
              </p>
            )}

            {/* Sisa plafon — DITAMPILKAN SEBELUM tombol ditekan. */}
            {form.po_id && (
              <div className="rounded border border-border p-3 text-sm"
                   data-testid="dp-form-plafon">
                {dpPoLoading || !plafon ? (
                  <span className="text-muted-foreground">
                    Menghitung sisa plafon...
                  </span>
                ) : plafon.tidakDiketahui ? (
                  <span className="text-danger-soft-foreground">
                    PO ini belum punya nilai total, jadi plafon uang muka tidak
                    bisa dihitung. Tetapkan vendor + harga PO lebih dulu.
                  </span>
                ) : (
                  <dl className="grid grid-cols-3 gap-x-4 gap-y-1">
                    <div>
                      <dt className="text-muted-foreground text-xs">
                        Plafon ({Math.round(meta.limits.cap_ratio * 100)}% nilai PO)
                      </dt>
                      <dd className="font-medium">{formatIDR(plafon.plafon)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">Sudah terpakai</dt>
                      <dd>{formatIDR(plafon.terpakai)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">Sisa</dt>
                      <dd className="font-semibold" data-testid="dp-form-sisa">
                        {formatIDR(Math.max(plafon.sisa, 0))}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Nominal (Rp)</label>
                <Input type="number" min="1" value={form.amount || ""}
                       data-testid="dp-form-amount"
                       onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
                {lewatPlafon && (
                  <p className="text-xs text-danger-soft-foreground mt-1"
                     data-testid="dp-form-over-cap">
                    Melebihi sisa plafon {formatIDR(Math.max(plafon.sisa, 0))}.
                    Turunkan nominalnya — backend akan menolaknya
                    (DP_EXCEEDS_PO_CAP).
                  </p>
                )}
                {form.amount !== "" && form.amount !== undefined && !nominalSah && (
                  <p className="text-xs text-danger-soft-foreground mt-1">
                    Nominal harus lebih dari nol.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Tanggal uang muka</label>
                <Input type="date" value={form.dp_date || ""}
                       data-testid="dp-form-date"
                       onChange={(e) => setForm(f => ({ ...f, dp_date: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Alasan pengajuan</label>
              <Textarea value={form.request_reason || ""} rows={3}
                        data-testid="dp-form-reason"
                        placeholder="Untuk apa uang ini dibayar di muka? Penyetuju perlu bisa menilainya."
                        onChange={(e) => setForm(f => ({ ...f, request_reason: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">
                Minimal {minAlasan} karakter — jawaban seperti "dp" atau
                "perlu" ditolak, karena penyetuju tidak bisa menilai apa pun
                dari itu.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Catatan (opsional)</label>
              <Textarea value={form.notes || ""} rows={2} data-testid="dp-form-notes"
                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setAjukanOpen(false)}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Mengajukan..." disabled={!bisaKirim}
                        data-testid="dp-form-submit" onClick={kirimAjukan}>
              Ajukan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
